/**
 * Client-side signup validation.
 *
 * Deliberate mirror of the server rules in `services/tenant/src/domain/
 * signup-input.ts` and `owner-password.ts`: the client validates for fast
 * feedback, the server remains the authority and its errors are shown inline on
 * the field it names.
 */
import {
  EMAIL_PATTERN,
  OWNER_PASSWORD_MIN_LENGTH,
  TEAM_NAME_MAX_LENGTH,
  TEAM_NAME_MIN_LENGTH,
  TENANT_SLUG_MAX_LENGTH,
  TENANT_SLUG_MIN_LENGTH,
  TENANT_SLUG_PATTERN,
} from '../../lib/constants';

export type SlugCheckState =
  | { status: 'idle' }
  | { status: 'checking'; slug: string }
  | { status: 'available'; slug: string }
  | { status: 'unavailable'; slug: string }
  | { status: 'error'; slug: string };

/** Derive a candidate team code from a team name. */
export function slugify(teamName: string): string {
  return teamName
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\da-z]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, TENANT_SLUG_MAX_LENGTH)
    .replace(/-+$/, '');
}

export function validateTeamName(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return 'Enter the team or program name.';
  if (trimmed.length < TEAM_NAME_MIN_LENGTH) {
    return `Team name must be at least ${TEAM_NAME_MIN_LENGTH} characters.`;
  }
  if (trimmed.length > TEAM_NAME_MAX_LENGTH) {
    return `Team name must be ${TEAM_NAME_MAX_LENGTH} characters or fewer.`;
  }
  return null;
}

export function validateSlug(value: string): string | null {
  if (value === '') return 'Enter a team code.';
  if (value.length < TENANT_SLUG_MIN_LENGTH) {
    return `Team code must be at least ${TENANT_SLUG_MIN_LENGTH} characters.`;
  }
  if (value.length > TENANT_SLUG_MAX_LENGTH) {
    return `Team code must be ${TENANT_SLUG_MAX_LENGTH} characters or fewer.`;
  }
  if (!TENANT_SLUG_PATTERN.test(value)) {
    return 'Use lower-case letters, numbers and single hyphens only (for example westview-eagles).';
  }
  return null;
}

/**
 * Who is typing the address: coaching staff (`'staff'`, the B2B default —
 * "work email") or a fan / family member (`'consumer'`, who has no work
 * email in this context). Only the empty-field message differs.
 */
export type EmailContext = 'staff' | 'consumer';

export function validateEmail(value: string, context: EmailContext = 'staff'): string | null {
  if (value.trim() === '') {
    return context === 'consumer' ? 'Enter your email address.' : 'Enter your work email address.';
  }
  if (!EMAIL_PATTERN.test(value.trim())) return 'Enter a valid email address.';
  return null;
}

/**
 * Password policy: ≥12 characters with an upper-case letter, a lower-case
 * letter, a digit and a symbol (services/tenant/src/domain/owner-password.ts).
 */
export function validatePassword(value: string): string | null {
  const missing: string[] = [];
  if (value.length < OWNER_PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${OWNER_PASSWORD_MIN_LENGTH} characters.`;
  }
  if (!/[A-Z]/.test(value)) missing.push('an upper-case letter');
  if (!/[a-z]/.test(value)) missing.push('a lower-case letter');
  if (!/\d/.test(value)) missing.push('a number');
  if (!/[^\dA-Za-z]/.test(value)) missing.push('a symbol');
  if (missing.length === 0) return null;
  return `Password is missing ${missing.join(', ')}.`;
}

export const PASSWORD_REQUIREMENT_HINTS: readonly string[] = [
  `At least ${OWNER_PASSWORD_MIN_LENGTH} characters`,
  'One upper-case letter',
  'One lower-case letter',
  'One number',
  'One symbol',
];

/**
 * Map a server error code to the wizard field it belongs to, so a 409 lands on
 * the offending input instead of a generic banner.
 */
export const SERVER_ERROR_FIELDS: Record<string, 'teamName' | 'slug' | 'email' | 'password'> = {
  INVALID_TEAM_NAME: 'teamName',
  INVALID_SLUG: 'slug',
  SLUG_TAKEN: 'slug',
  INVALID_EMAIL: 'email',
  EMAIL_IN_USE: 'email',
  WEAK_PASSWORD: 'password',
};
